import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit,
  NgZone,
  inject,
} from '@angular/core';
import {
  ICliente,
  IFilter,
  IGeoJSONCircle,
  IPopulate,
  IQueryParam,
  IRecorrido,
  IRecorridoDinamico,
  IReporteBase,
  IReporteGenerico,
  IUbicacion,
  IUpdateUsuario,
  ISocketMessage,
} from 'modelos/src';
import { ConfigMarkersService } from '../../../../auxiliares/servicios/http/config-markers.service';
import { Subscription, debounceTime } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../../login/login.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Stop } from 'modelos/src';
import { MapInstanceFilter, SeleccionadosBarraLateral } from './interfaces';
import { MapaVehiculosService } from './mapaVehiculos.service';
import { BarraLateralComponent } from './barra-lateral/barra-lateral.component';
import { ClientesJerarquiaService } from './barra-lateral/services/clientes-jerarquia.service';
import { WebSocketService } from '../../../../auxiliares/servicios/websocket.service';
import { UsuariosService } from '../../../../auxiliares/servicios/http/usuarios.service';

// Ancho del panel de acciones (a la derecha del mapa). Se guarda en
// localStorage con el mismo criterio que el menú de navegación.
const SIDEBAR_WIDTH_KEY = 'mapaVehiculosSidebarWidth';
const MIN_SIDEBAR_WIDTH = 280;
const MAX_SIDEBAR_WIDTH = 700;

@Component({
  selector: 'app-mapa-contenedor-mapas',
  templateUrl: './contenedor-mapa.component.html',
  styleUrls: ['./contenedor-mapa.component.scss'],
  standalone: false,
})
export class ContenedorMapaComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private lista2 = inject(Lista2Service);
  private usuariosService = inject(UsuariosService);
  private breakpointObserver = inject(BreakpointObserver);
  mapaService = inject(MapaVehiculosService);
  private configMarkersService = inject(ConfigMarkersService);
  private jerarquia = inject(ClientesJerarquiaService);
  private ngZone = inject(NgZone);
  private wsService = inject(WebSocketService);

  @ViewChild('sidenavContainer') sidenavContainer?: MatSidenavContainer;
  @ViewChild('sidenavContainer', { read: ElementRef })
  sidenavContainerEl?: ElementRef<HTMLElement>;
  @ViewChild('sidenav') sidenav?: MatSidenav;
  @ViewChild('sidenav', { read: ElementRef })
  sidenavEl?: ElementRef<HTMLElement>;
  @ViewChild('mapaIndividual') mapaIndividual?: any;
  @ViewChild(BarraLateralComponent) barraLateral?: BarraLateralComponent;

  // Subscripciones locales para datos externos
  private reportes$?: Subscription;
  private ubicaciones$?: Subscription;
  private recorridos$?: Subscription;
  private recorridosDinamicos$?: Subscription;
  private refrescarReportes$?: Subscription;
  private webSocketSub$?: Subscription;

  // Pedidos "por id" del reporte completo del vehículo seleccionado (one-shot).
  private detalles$ = new Subscription();
  // activo._id → valores.timestamp del último detalle traído. Sirve para no
  // volver a pedirlo mientras el vehículo no se haya movido.
  private timestampDetalle = new Map<string, string>();

  // Clave para sessionStorage
  private readonly SESSION_STORAGE_KEY = 'vehiculos_seleccionadosBarraLateral';

  //Cliente propio
  public clientePropio: ICliente = LoginService.getClientePropio();

  //Seguimiento de vehículo
  public followTargetId?: string | undefined;

  // Tracking de suscripciones activas para múltiples mapas
  private activeSubscriptions = new Set<string>();
  // Último vehículo seleccionado (y suscripto) en cada mapa. La barra lateral
  // escribe la nueva selección en el estado ANTES de emitir, así que el anterior
  // no se puede leer de ahí.
  private idSeleccionadoPorMapa = new Map<number, string | undefined>();

  // SIDENAV / responsive
  public isHandset = false;
  public sidenavMode: 'side' | 'over' = 'side';
  private subs = new Subscription();

  // ANCHO DEL SIDENAV (arrastrable, igual que el menú de navegación)
  public readonly minSidebarWidth = MIN_SIDEBAR_WIDTH;
  public sidebarWidth = this.leerAnchoSidebar();
  public resizingSidebar = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  // Propiedades computadas que acceden al servicio
  get reportes(): IReporteBase<'Tracker 4G'>[] {
    return this.mapaService.reportes;
  }

  get ubicaciones(): IUbicacion[] {
    return this.mapaService.ubicaciones;
  }

  get recorridos(): IRecorrido[] {
    return this.mapaService.recorridos;
  }

  get recorridosDinamicos(): IRecorridoDinamico[] {
    return this.mapaService.recorridosDinamicos;
  }

  get cantMapas(): number {
    return this.mapaService.cantMapas;
  }

  get mapaSeleccionado(): number {
    return this.mapaService.mapaSeleccionado;
  }

  get loadingMapa(): boolean {
    return this.mapaService.loadingMapa;
  }

  get paradaSeleccionada(): Stop | undefined {
    return this.mapaService.paradaSeleccionada;
  }

  get circuloBusqueda(): IGeoJSONCircle | undefined {
    return this.mapaService.circuloBusqueda;
  }

  get seleccionadosBarraLateral(): SeleccionadosBarraLateral[] {
    return this.mapaService.seleccionadosBarraLateral;
  }

  get seleccionadoBarraLateral(): SeleccionadosBarraLateral | undefined {
    return this.mapaService.getSeleccionadoActual();
  }

  get opened(): boolean {
    return this.mapaService.sidenavOpened;
  }

  set opened(value: boolean) {
    this.mapaService.setSidenavOpened(value);
  }

  get hidden(): boolean {
    return this.mapaService.sidenavHidden;
  }

  set hidden(value: boolean) {
    this.mapaService.setSidenavHidden(value);
  }

  get tipoMapaBaseActual(): string | undefined {
    return this.mapaIndividual?.tipoMapaBase;
  }

  get reporteSeleccionado(): IReporteBase<'Tracker 4G'> | undefined {
    return this.mapaService.reporteSeleccionadoPorMapa[this.mapaSeleccionado];
  }

  set reporteSeleccionado(val: IReporteBase<'Tracker 4G'> | undefined) {
    this.mapaService.setReporteSeleccionadoPorMapa(this.mapaSeleccionado, val);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // LISTADOS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  private async listarConfigMarkers(): Promise<void> {
    try {
      const query: IQueryParam = {
        filter: JSON.stringify({}),
        populate: JSON.stringify([]),
        includeChildren: true,
      };
      const result = await this.configMarkersService.getFiltered(query);
      this.mapaService.setConfigMarkers(result.datos);
    } catch (error) {
      console.error('Error al cargar config markers:', error);
    }
  }

  private async listarReportes(): Promise<void> {
    const fechaLimite = new Date();
    fechaLimite.setDate(fechaLimite.getDate() - 1); // Últimas 24 horas

    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Vehiculo',
      // Un vehículo puede tener tracker, dashcam o los dos. El de la dashcam
      // sólo se usa si el vehículo no tiene tracker (ver el filtro de abajo).
      tipoReporte: { $in: ['Tracker 4G', 'Dashcam 4G'] },
      'valores.timestamp': { $gte: fechaLimite.toISOString() },
    } as any;

    // Payload mínimo: SOLO lo que dibuja el marker, lo que filtra la barra
    // lateral y lo que se busca por texto (identificación/patente/grupo: la
    // búsqueda del panel hace JSON.stringify del reporte). Todo lo demás
    // (dirección, odómetro, marca, chofer, cliente, recorrido asignado) se pide
    // por id del vehículo seleccionado en cargarDetalleSeleccionado(). Con ~700
    // vehículos cada 60s, cada campo de más se paga 700 veces por tick.
    const populate: IPopulate = [
      {
        path: 'activo',
        select:
          'identificacion categoria funcion idGrupo idCliente idTracker vehiculo.tipo vehiculo.patente imagenMiniatura',
        populate: [{ path: 'grupo', select: 'nombre color' }],
      },
      {
        path: 'grupos',
        select: 'nombre color',
      },
    ];

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      includeChildren: true,
      // Sin fechaCreacion: la dedup por activo usa valores.timestamp, que
      // siempre viene en estos reportes (el `?? fechaCreacion` es un resto
      // histórico — tampoco se pedía antes).
      // tipoReporte: el detalle del seleccionado se pide del mismo tipo.
      select:
        'idEntidad idsAsignados idCliente tipoReporte ' +
        'valores.timestamp valores.geojson valores.snappedGeojson ' +
        'valores.velocidad valores.ignicion valores.orientacion',
      unlimited: true,
      // Nadie lee totalCount acá (con unlimited es datos.length) y el count
      // corre en paralelo al find sobre el mismo filtro: es la mitad del
      // trabajo de la query, tirado.
      excludeTotalCount: true,
    };

    // Polling de reconciliación (antes 15s). El movimiento/recoloreo en vivo lo
    // maneja el WS (subscribe-all-vehicles); el poll solo refresca altas/bajas y
    // corrige drift si se perdiera algún mensaje. Intervalo más alto = menos carga.
    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      // La ventana de 24h se recalcula en cada tick: armada una sola vez
      // quedaba anclada a la carga de la página y con la pestaña abierta
      // horas nunca dejaban de entrar los vehículos que ya no reportan.
      .ultimosReportesGenericos<'Tracker 4G'>(() => {
        const desde = new Date();
        desde.setDate(desde.getDate() - 1);
        return {
          ...query,
          filter: JSON.stringify({
            ...filter,
            'valores.timestamp': { $gte: desde.toISOString() },
          }),
        };
      }, 60)
      .subscribe((data) => {
        let reportesFiltrados = data.datos;
        reportesFiltrados = reportesFiltrados.filter(
          (r) =>
            r.activo?.categoria === 'Vehículo' &&
            r.activo?.vehiculo?.tipo !== 'Colectivo',
        );
        // El mapa muestra siempre la posición del tracker; la de la dashcam,
        // sólo para los vehículos que no tienen tracker.
        reportesFiltrados = reportesFiltrados.filter(
          (r) =>
            (r.tipoReporte as string) !== 'Dashcam 4G' || !r.activo?.idTracker,
        );

        // Deduplicar por activo._id: si un vehículo tiene múltiples trackers,
        // mantener solo el reporte más reciente para evitar markers duplicados
        const reportesPorActivo = new Map<string, IReporteBase<'Tracker 4G'>>();
        for (const r of reportesFiltrados) {
          const activoId = r.activo?._id;
          if (!activoId) continue;
          const existing = reportesPorActivo.get(activoId);
          if (!existing) {
            reportesPorActivo.set(activoId, r);
          } else {
            const tsNew = (r.valores as any)?.timestamp ?? r.fechaCreacion;
            const tsExisting =
              (existing.valores as any)?.timestamp ?? existing.fechaCreacion;
            if (tsNew > tsExisting) {
              reportesPorActivo.set(activoId, r);
            }
          }
        }
        // Incluir también reportes sin activo (no deduplicar)
        const sinActivo = reportesFiltrados.filter((r) => !r.activo?._id);
        reportesFiltrados = [...reportesPorActivo.values(), ...sinActivo];

        this.mapaService.setReportes(reportesFiltrados);
        // Actualizar el reporte seleccionado si hay un vehículo en seguimiento
        this.actualizarReporteSeguido(reportesFiltrados);

        // Verificar si hay una selección de vehículo pendiente (ej: desde tratamiento de eventos)
        this.aplicarPendingVehicleSelection(reportesFiltrados);
      });
    this.mapaService.setLoadingMapa(false);
  }

  /**
   * Actualiza el reporte seleccionado cuando llega un nuevo reporte del vehículo seguido
   * o simplemente seleccionado (con o sin seguimiento activo)
   */
  private actualizarReporteSeguido(
    reportes: IReporteBase<'Tracker 4G'>[],
  ): void {
    const seleccionadoActual = this.mapaService.getSeleccionadoActual();
    const followId = seleccionadoActual?.followTargetId;
    const reporteSeleccionadoId = this.reporteSeleccionado?.activo?._id;

    // Actualizar si hay seguimiento activo O si hay un vehículo seleccionado
    const vehiculoId = followId || reporteSeleccionadoId;

    if (vehiculoId) {
      const reporteActualizado = reportes.find(
        (r) => r.activo?._id === vehiculoId,
      );

      if (reporteActualizado) {
        // Actualizar el reporte seleccionado con los nuevos datos
        this.reporteSeleccionado = reporteActualizado;

        // Actualizar también en el estado de barra lateral
        this.mapaService.updateSeleccionadoBarraLateral({
          reporteSeleccionado: reporteActualizado,
        });

        // Backup del canal en vivo del card: el card del mismo vehículo se
        // alimenta de reporteSeleccionadoPorMapa$ (WS). Refrescarlo también en el
        // poll cubre el caso de que se pierdan mensajes WS.
        this.mapaService.setReporteSeleccionadoPorMapa(
          this.mapaService.mapaSeleccionado,
          reporteActualizado,
        );

        // El poll trae el liviano: si el vehículo se movió desde el último
        // detalle, los campos que solo vienen por id (dirección, odómetro)
        // quedaron viejos. Backup del mismo caso de arriba (pérdida de WS).
        const tsNuevo = (reporteActualizado.valores as any)?.timestamp;
        if (tsNuevo && this.timestampDetalle.get(vehiculoId) !== tsNuevo) {
          this.cargarDetalleSeleccionado(reporteActualizado);
        }
      }
    }
  }

  /**
   * Trae el reporte COMPLETO del vehículo seleccionado (uno solo) y lo fusiona
   * sobre el liviano del listado. El listado del mapa manda lo mínimo para
   * dibujar el marker, así que todo lo que muestra el card sale de acá.
   *
   * Filtra por {idCliente, tipoReporte, idEntidad}: es el prefijo exacto del
   * índice único de ultimoreportegenericos, así que resuelve con un IXSCAN de
   * 1 documento. El fallback por idsAsignados es el mismo filtro que ya usan
   * las consultas de combustible/temperatura de info-vehiculo.
   */
  private cargarDetalleSeleccionado(r?: IReporteBase<'Tracker 4G'>): void {
    if (!r) return;

    const idActivo = r.activo?._id;
    // El del marker: el de la dashcam si el vehículo no tiene tracker.
    const tipoReporte = (r.tipoReporte as string) ?? 'Tracker 4G';
    const filter: Record<string, unknown> =
      r.idEntidad && r.idCliente
        ? {
            idCliente: r.idCliente,
            tipoReporte,
            idEntidad: r.idEntidad,
          }
        : idActivo
          ? { tipoReporte, idsAsignados: idActivo }
          : {};
    if (!Object.keys(filter).length) return;

    const populate: IPopulate = [
      {
        path: 'activo',
        select:
          'identificacion categoria funcion idGrupo idCliente vehiculo imagenMiniatura',
        populate: [{ path: 'grupo', select: 'nombre color' }],
      },
      { path: 'grupos', select: 'nombre color' },
      { path: 'cliente', select: 'nombre tipoCliente' },
    ];

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      includeChildren: true,
      // `valores` entero: es UN documento y así el card no se rompe si mañana
      // muestra otro campo del reporte.
      select: 'idEntidad idsAsignados idCliente fechaCreacion valores',
      limit: 1,
      excludeTotalCount: true,
    };

    this.detalles$.add(
      this.lista2
        .ultimosReportesGenericos<'Tracker 4G'>(query, 0)
        .subscribe((data) => {
          const detalle = data.datos?.[0];
          if (!detalle) return;

          const merged = this.mapaService.aplicarDetalleReporte(detalle);
          if (!merged) return;

          const vehiculoId = merged.activo?._id;
          const ts = (merged.valores as any)?.timestamp;
          if (vehiculoId && ts) this.timestampDetalle.set(vehiculoId, ts);
        }),
    );
  }

  private setupRefrescarReportes(): void {
    this.refrescarReportes$ = this.mapaService.refrescarReportes$.subscribe(
      () => this.listarReportes(),
    );
  }

  private async listarUbicaciones(): Promise<void> {
    const filter: IFilter<IUbicacion> = {
      categoria: 'Vehiculos',
    };

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'identificacion',
      select: 'idCliente identificacion geojson color',
      includeChildren: true,
    };

    this.ubicaciones$?.unsubscribe();
    this.ubicaciones$ = this.lista2.ubicacions(query, 30).subscribe({
      next: (data) => {
        // Solo actualizar si hay datos válidos
        if (data.datos && data.datos.length > 0) {
          this.mapaService.setUbicaciones(data.datos);
        } else if (data.datos && data.datos.length === 0) {
          const ubicacionesActuales = this.mapaService.ubicaciones;
          if (ubicacionesActuales.length === 0) {
            this.mapaService.setUbicaciones([]);
          }
        }
      },
      error: (err) => {
        console.error('Error al cargar ubicaciones:', err);
      },
    });
  }

  private async listarRecorridos(): Promise<void> {
    const filter: IFilter<IRecorrido> = { categoria: 'Vehiculo' };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'nombre',
      select: 'idCliente nombre geojson color',
      includeChildren: true,
    };

    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.lista2.recorridos(query, 30).subscribe({
      next: (data) => {
        // Solo actualizar si hay datos válidos
        // Preservar recorridos existentes si la respuesta está vacía (puede ser un error temporal)
        if (data.datos && data.datos.length > 0) {
          this.mapaService.setRecorridos(data.datos);
        } else if (data.datos && data.datos.length === 0) {
          // Solo limpiar si explícitamente recibimos array vacío en la primera carga
          const recorridosActuales = this.mapaService.recorridos;
          if (recorridosActuales.length === 0) {
            // Primera carga y no hay recorridos, actualizar
            this.mapaService.setRecorridos([]);
          }
          // Si ya había recorridos, no los borramos por un error temporal de la API
        }
      },
      error: (err) => {
        console.error('Error al cargar recorridos:', err);
        // No hacer nada, preservar recorridos existentes
      },
    });
  }

  private async listarRecorridosDinamicos(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'idCliente nombre geojson color paradas',
      includeChildren: true,
    };

    this.recorridosDinamicos$?.unsubscribe();
    this.recorridosDinamicos$ = this.lista2
      .recorridosDinamicos(query, 30)
      .subscribe({
        next: (data) => {
          if (data.datos && data.datos.length > 0) {
            this.mapaService.setRecorridosDinamicos(data.datos);
          } else if (data.datos && data.datos.length === 0) {
            const actuales = this.mapaService.recorridosDinamicos;
            if (actuales.length === 0) {
              this.mapaService.setRecorridosDinamicos([]);
            }
          }
        },
        error: (err) => {
          console.error('Error al cargar recorridos dinámicos:', err);
        },
      });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //SETEADOS DESDE OTROS COMPONENTES
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * Si hay un vehículo pendiente de selección (seteado desde otro componente, como el de evento),
   * lo selecciona en el mapa actual y limpia el pending.
   */
  private aplicarPendingVehicleSelection(
    reportes: IReporteBase<'Tracker 4G'>[],
  ): void {
    const pendingId = this.mapaService.pendingVehicleActivoId;
    if (!pendingId) return;

    const reporte = reportes.find((r) => r.activo?._id === pendingId);
    if (reporte) {
      this.reporteSeleccionado = reporte;
      // Igual que en una selección normal: el listado es liviano.
      this.cargarDetalleSeleccionado(reporte);
      this.mapaService.updateSeleccionadoBarraLateral({
        reporteSeleccionado: reporte,
        detallesVehiculoOpen: true,
      });

      // Abrir barra lateral de detalles
      if (this.barraLateral) {
        this.barraLateral.detallesVehiculoOpen = true;
      }

      // Pan único al vehículo sin activar seguimiento continuo
      const valores = reporte.valores as any;
      const geojson = valores?.snappedGeojson || valores?.geojson;
      if (geojson?.coordinates) {
        const [lng, lat] = geojson.coordinates as [number, number];
        this.mapaService.emitPanOnce([lng, lat]);
      }
    }

    this.mapaService.clearPendingVehicleActivoId();
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //DETECTORES DE CAMBIOS EN LA BARRA LATERAL
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  //Cambio en el reporte seleccionado desde barra lateral
  public onReporteChange(r?: IReporteBase<'Tracker 4G'>) {
    // Capturar el seleccionado anterior (ver idSeleccionadoPorMapa).
    const previousSelectedId =
      this.idSeleccionadoPorMapa.get(this.mapaSeleccionado) ??
      this.mapaService.getSeleccionadoActual()?.reporteSeleccionado?.idEntidad;
    this.idSeleccionadoPorMapa.set(this.mapaSeleccionado, r?.idEntidad);

    // Actualizar el reporte seleccionado localmente
    this.reporteSeleccionado = r;

    // Si no hay reporte, limpiar el seguimiento
    if (!r) {
      const previousVehicleId = this.followTargetId;

      // Limpiar el estado en el servicio PRIMERO (antes de unsubscribe)
      this.mapaService.updateSeleccionadoBarraLateral({
        followTargetId: undefined,
        reporteSeleccionado: undefined,
      });

      // Actualizar local
      this.followTargetId = undefined;

      // Desuscribirse del vehículo anterior si existe
      // (unsubscribeFromVehicle verifica si otros mapas lo necesitan)
      if (previousVehicleId) {
        this.unsubscribeFromVehicle(previousVehicleId);
      }
      // Desuscribir el seleccionado anterior (su suscripción era para el card).
      if (previousSelectedId && previousSelectedId !== previousVehicleId) {
        this.unsubscribeFromVehicle(previousSelectedId);
      }
      // Sin selección el reporte vuelve a ser el liviano del listado.
      this.olvidarDetalle(
        previousVehicleId,
        previousSelectedId && !this.vehiculoEnUso(previousSelectedId)
          ? previousSelectedId
          : undefined,
      );
    } else if (r.idEntidad) {
      // Selección desde la barra lateral: suscribir para recibir payload completo.
      this.subscribeToVehicle(r.idEntidad);
      this.cargarDetalleSeleccionado(r);
      // Liberar el seleccionado anterior (si ningún mapa lo sigue/selecciona);
      // sin esto cada vehículo elegido quedaba suscripto hasta recargar.
      if (previousSelectedId && previousSelectedId !== r.idEntidad) {
        this.unsubscribeFromVehicle(previousSelectedId);
        if (!this.vehiculoEnUso(previousSelectedId)) {
          this.olvidarDetalle(previousSelectedId);
        }
      }
    }
  }

  // Deja de preservar el detalle de los vehículos que ya no están seleccionados.
  private olvidarDetalle(...vehicleIds: (string | undefined)[]): void {
    for (const vehicleId of vehicleIds) {
      if (!vehicleId) continue;
      this.mapaService.olvidarDetalleReporte(vehicleId);
      const reporte = this.reportes.find(
        (r) => r.idEntidad === vehicleId || r.idsAsignados?.includes(vehicleId),
      );
      const activoId = reporte?.activo?._id;
      if (activoId) this.timestampDetalle.delete(activoId);
    }
  }

  //Cerrar detalle del reporte
  public onClose() {
    this.olvidarDetalle(this.reporteSeleccionado?.idEntidad);
    this.reporteSeleccionado = undefined;
  }

  //Cambios en el reporte seleccionado (desde el mapa)
  public reporteSeleccionadoChange(data: {
    reporte: IReporteBase<'Tracker 4G'>;
    mapaNum: number;
  }) {
    const { reporte: r, mapaNum } = data;

    // Permitir que el usuario pueda hacer click en otros vehículos en el mismo mapa
    this.reporteSeleccionado = r;

    // Obtener el estado del mapa específico donde ocurrió el click
    const seleccionadoDelMapa =
      this.mapaService.getSeleccionadoPorNumero(mapaNum);
    const tieneFollowActive = seleccionadoDelMapa?.followTargetId !== undefined;

    // B: suscribir el seleccionado por-vehículo para recibir el payload COMPLETO
    // (el card del seleccionado necesita todos los campos en vivo, no el liviano).
    const previousSelectedId =
      seleccionadoDelMapa?.reporteSeleccionado?.idEntidad;
    this.idSeleccionadoPorMapa.set(mapaNum, r?.idEntidad);
    if (r?.idEntidad) {
      this.subscribeToVehicle(r.idEntidad);
    }
    // El reporte del listado es liviano: traer el completo de ESTE vehículo.
    this.cargarDetalleSeleccionado(r);

    // Si hay seguimiento activo EN ESTE MAPA, cambiar el seguimiento al nuevo vehículo seleccionado
    if (tieneFollowActive) {
      const newVehicleId = r.idEntidad;
      const previousVehicleId = seleccionadoDelMapa?.followTargetId;

      // Suscribirse al nuevo vehículo (subscribeToVehicle verifica duplicados)
      if (newVehicleId && newVehicleId !== previousVehicleId) {
        this.subscribeToVehicle(newVehicleId);
      }

      // Actualizar followTargetId local si este es el mapa actual
      if (mapaNum === this.mapaSeleccionado) {
        this.followTargetId = newVehicleId;
      }

      // Actualizar el estado específico de este mapa
      this.mapaService.updateSeleccionadoBarraLateralPorNumero(mapaNum, {
        followTargetId: newVehicleId,
        reporteSeleccionado: r,
      });

      // Limpiar suscripción del vehículo anterior si ya no es necesario
      // (unsubscribeFromVehicle verifica si otros mapas lo necesitan)
      if (previousVehicleId && previousVehicleId !== newVehicleId) {
        this.unsubscribeFromVehicle(previousVehicleId);
      }
    } else {
      // Sin seguimiento activo, solo actualizar el reporte seleccionado
      this.mapaService.updateSeleccionadoBarraLateralPorNumero(mapaNum, {
        reporteSeleccionado: r,
      });
    }

    // Desuscribir el vehículo previamente seleccionado en este mapa si cambió y
    // ya no lo necesita nadie (el estado ya refleja la nueva selección arriba).
    if (previousSelectedId && previousSelectedId !== r?.idEntidad) {
      this.unsubscribeFromVehicle(previousSelectedId);
      this.olvidarDetalle(previousSelectedId);
    }

    // La sección de detalles NO se abre sola al clickear un vehículo en el
    // mapa: la despliega el usuario cuando la quiere ver.
  }

  //Cambios en la zona seleccionada (desde el mapa)
  public ubicacionSeleccionadaChange(data: {
    ubicacion?: IUbicacion;
    mapaNum: number;
  }) {
    const { ubicacion, mapaNum } = data;

    const seleccionadoDelMapa =
      this.mapaService.getSeleccionadoPorNumero(mapaNum);
    if (seleccionadoDelMapa?.ubicacionSeleccionada?._id === ubicacion?._id) {
      return;
    }

    this.mapaService.updateSeleccionadoBarraLateralPorNumero(mapaNum, {
      ubicacionSeleccionada: ubicacion,
    });
  }

  //Seleccionados de la barra lateral
  public async onSeleccionadosBarraLateralChange(
    seleccionados: SeleccionadosBarraLateral[],
  ) {
    //Primero guarda todos los seleccionados
    // Esto disparará automáticamente el guardado en sessionStorage a través de la suscripción
    this.mapaService.setSeleccionadosBarraLateral(seleccionados);
  }

  //Esto es para detectar cambios desde el mapa que afectan a lo que se muestra en la barra lateral
  public async onSeleccionadoBarraLateralChange(
    seleccionado: SeleccionadosBarraLateral,
  ) {
    // Actualizar el array completo con el cambio individual
    if (seleccionado) {
      const current = [...this.seleccionadosBarraLateral];
      const index = current.findIndex(
        (s) => s.mapaSeleccionado === seleccionado.mapaSeleccionado,
      );

      if (index !== -1) {
        // Actualizar el existente
        current[index] = seleccionado;
      } else {
        // Agregar uno nuevo
        current.push(seleccionado);
      }

      // Esto disparará automáticamente el guardado en sessionStorage a través de la suscripción
      this.mapaService.setSeleccionadosBarraLateral(current);
    }
  }

  //Cambio en el mapa seleccionado
  public async onMapaSeleccionadoChange(m: number) {
    // Aceptar sólo si m está dentro del rango actual
    const newM = Math.max(
      1,
      Math.min(this.cantMapas, Math.floor(Number(m) || 1)),
    );
    this.mapaService.setMapaSeleccionado(newM);

    // Si hay un vehículo siendo seguido, buscar su reporte y mostrarlo
    const seleccionadoActual = this.mapaService.getSeleccionadoActual();
    const followId = seleccionadoActual?.followTargetId;
    if (followId) {
      const reporteSeguido = this.reportes.find(
        (r) => r.activo?._id === followId,
      );
      if (reporteSeguido) {
        this.reporteSeleccionado = reporteSeguido;

        // Actualizar también en el estado de barra lateral
        if (seleccionadoActual) {
          seleccionadoActual.reporteSeleccionado = reporteSeguido;
          this.onSeleccionadoBarraLateralChange(seleccionadoActual);
        } else {
          // Si no existe el seleccionado para este mapa, crearlo
          const nuevoSeleccionado: SeleccionadosBarraLateral = {
            mapaSeleccionado: this.mapaSeleccionado,
            reporteSeleccionado: reporteSeguido,
            followTargetId: followId,
          };
          this.onSeleccionadoBarraLateralChange(nuevoSeleccionado);
        }
      }
    }

    // Al cambiar de mapa, el getter/setter de reporteSeleccionado ya gestiona el detalle por mapa
  }

  //Cambio en el tipo de mapa base desde la barra lateral
  public onTipoMapaBaseChange(tipo: string) {
    if (this.mapaIndividual) {
      this.mapaIndividual.cambiarCapaBase(tipo);
    }
  }

  //Toggle del popup de ayuda
  public onToggleHelp() {
    if (this.mapaIndividual) {
      this.mapaIndividual.toggleHelp();
    }
  }

  //Búsqueda de vehículos
  public onBusquedaVehiculos(data: {
    reportes: IReporteBase<'Tracker 4G'>[];
    mapIndex: number;
    clearSearch?: boolean;
  }) {
    // Si es una limpieza de búsqueda, limpiar el filtro para mostrar todos
    if (data.clearSearch) {
      this.mapaService.clearVehiculosFiltradosPorMapa(data.mapIndex);
      return;
    }

    // Si hay búsqueda activa, establecer los IDs filtrados (incluso si es 0 resultados)
    this.mapaService.setVehiculosFiltradosPorMapa(data.mapIndex, data.reportes);

    // if (this.mapaIndividual) {
    //   this.mapaIndividual.centrarEnVehiculos(data.reportes, data.mapIndex);
    // }
  }

  //CONTROL DEL SIDENAV
  // Cuando se presiona el botón flotante o el del header:
  // - en handset: abre/cierra overlay (opened)
  // - en desktop: oculta/mostrar completamente (hidden)
  public toggleSidenav() {
    if (this.isHandset) {
      this.opened = !this.opened;
    } else {
      this.hidden = !this.hidden;
    }
  }

  // cerrar cuando se toca el backdrop (en modo over)
  public onBackdropClick() {
    if (this.isHandset) {
      this.opened = false;
    }
  }

  //ANCHO DEL SIDENAV
  /** Ancho guardado, o el 30% de la ventana la primera vez. */
  private leerAnchoSidebar(): number {
    const guardado = parseInt(
      localStorage.getItem(SIDEBAR_WIDTH_KEY) || '',
      10,
    );
    const ancho = Number.isFinite(guardado)
      ? guardado
      : Math.round(window.innerWidth * 0.3);
    return this.acotarAnchoSidebar(ancho);
  }

  private maxSidebarWidth(): number {
    return Math.max(
      MIN_SIDEBAR_WIDTH,
      Math.min(MAX_SIDEBAR_WIDTH, window.innerWidth - 200),
    );
  }

  private acotarAnchoSidebar(ancho: number): number {
    return Math.min(Math.max(ancho, MIN_SIDEBAR_WIDTH), this.maxSidebarWidth());
  }

  /**
   * El panel y el contenido del mapa tienen transiciones de `width` y `margin`.
   * Durante el arrastre hay que apagarlas o cada mousemove reinicia la
   * animación y el panel parece congelado hasta que se suelta el clic.
   * Se togglea por DOM (y no por binding) para que valga desde el primer
   * mousemove, sin depender de un ciclo de detección de cambios.
   */
  private marcarResizing(activo: boolean): void {
    for (const ref of [this.sidenavEl, this.sidenavContainerEl]) {
      ref?.nativeElement.classList.toggle('resizing', activo);
    }
  }

  // El panel está a la derecha (position="end"): arrastrar hacia la izquierda lo ensancha.
  public startResizeSidebar(event: MouseEvent): void {
    this.resizingSidebar = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarWidth;
    this.marcarResizing(true);
    event.preventDefault();
  }

  @HostListener('window:mousemove', ['$event'])
  public onResizeSidebar(event: MouseEvent): void {
    if (!this.resizingSidebar) return;

    const delta = this.resizeStartX - event.clientX;
    this.sidebarWidth = this.acotarAnchoSidebar(this.resizeStartWidth + delta);
    // Escribir el ancho a mano además del binding: el repintado acompaña al
    // mouse aunque la detección de cambios llegue un tick más tarde.
    if (this.sidenavEl) {
      this.sidenavEl.nativeElement.style.width = `${this.sidebarWidth}px`;
    }
    this.sidenavContainer?.updateContentMargins();
  }

  @HostListener('window:mouseup')
  public stopResizeSidebar(): void {
    if (!this.resizingSidebar) return;

    this.resizingSidebar = false;
    this.marcarResizing(false);
    localStorage.setItem(SIDEBAR_WIDTH_KEY, this.sidebarWidth.toString());
    this.sidenavContainer?.updateContentMargins();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    const acotado = this.acotarAnchoSidebar(this.sidebarWidth);
    if (acotado !== this.sidebarWidth) {
      this.sidebarWidth = acotado;
      this.sidenavContainer?.updateContentMargins();
    }
  }

  //FILTRO INICIAL
  /**
   * Deja cada mapa filtrando por el cliente actual del permiso y su jerarquía.
   * Es un filtro ÚNICO: aplica igual a vehículos, ubicaciones y recorridos.
   * No pisa los filtros que ya tenga un mapa (los del panel de filtros).
   */
  private async aplicarFiltroClientePorDefecto(): Promise<void> {
    const filtro: MapInstanceFilter = await this.jerarquia.filtroPorDefecto();
    if (!filtro.idsClientes?.length) return;

    for (let mapa = 1; mapa <= this.cantMapas; mapa++) {
      if (this.mapaService.getSeleccionadoPorNumero(mapa)?.filtrosSeleccionados)
        continue;

      this.mapaService.setFiltrosPorMapa(mapa, filtro);
      this.mapaService.updateSeleccionadoBarraLateralPorNumero(mapa, {
        filtrosSeleccionados: filtro,
      });
    }
  }

  //MÉTODOS PARA SESSIONstorage
  // Guardar seleccionados en sessionStorage
  private saveSeleccionadosToSessionStorage(
    seleccionados: SeleccionadosBarraLateral[],
  ): void {
    try {
      sessionStorage.setItem(
        this.SESSION_STORAGE_KEY,
        JSON.stringify(seleccionados),
      );
    } catch (error) {
      console.error('Error al guardar en sessionStorage:', error);
    }
  }

  // Cargar seleccionados desde sessionStorage
  private loadSeleccionadosFromSessionStorage():
    | SeleccionadosBarraLateral[]
    | undefined {
    try {
      const data = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data) as SeleccionadosBarraLateral[];
        // IMPORTANTE: Limpiar filtrosSeleccionados al cargar desde sessionStorage
        // ya que es un estado transitorio que no debe persistir entre cargas.
        // Si se mantiene, puede causar que no se muestren vehículos al iniciar.
        if (Array.isArray(parsed)) {
          return parsed.map(
            (item): SeleccionadosBarraLateral => ({
              ...item,
              filtrosSeleccionados: undefined,
            }),
          );
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error al cargar desde sessionStorage:', error);
    }
    return undefined;
  }

  // WebSocket Setup para tracking en tiempo real
  private setupWebSocket(): void {
    // Suscribirse a mensajes WebSocket
    this.webSocketSub$ = this.wsService
      .getMessage()
      .subscribe((msg: ISocketMessage) => {
        if (!msg.paths?.includes('reportegenerico') || msg.method !== 'post') {
          return;
        }

        // Batch del mapa (all-vehicles): body es un array de reportes livianos.
        // Un solo mensaje por ventana → mucho menos overhead que 1 msg/reporte.
        if (msg.motivo === 'vehiculos-batch') {
          const reportes = msg.body as any as IReporteBase<'Tracker 4G'>[];
          if (Array.isArray(reportes)) {
            // Una sola llamada → merge O(1) por vehículo + una emisión rxjs.
            this.mapaService.updateReportesBatch(
              reportes.filter(
                (r) => r && (r as any).tipoReporte === 'Tracker 4G',
              ),
            );
          }
          return;
        }

        // Per-vehículo (seleccionado/seguido): payload completo, un reporte.
        if (msg.motivo === 'vehiculo-suscrito') {
          const reporte = msg.body as IReporteBase<'Tracker 4G'>;
          if (reporte && (reporte as any).tipoReporte === 'Tracker 4G') {
            this.mapaService.updateSingleReporte(reporte);
          }
        }
      });
  }

  // Suscribir a un vehículo en tiempo real
  private subscribeToVehicle(vehicleId: string): void {
    // Solo suscribir si no está ya en el Set (evita suscripciones duplicadas)
    if (!this.activeSubscriptions.has(vehicleId)) {
      this.wsService.subscribeVehicle(vehicleId);
      this.activeSubscriptions.add(vehicleId);
    }
  }

  // Algún mapa lo SIGUE o lo tiene SELECCIONADO.
  private vehiculoEnUso(vehicleId: string): boolean {
    return this.seleccionadosBarraLateral.some(
      (s) =>
        s.followTargetId === vehicleId ||
        s.reporteSeleccionado?.idEntidad === vehicleId,
    );
  }

  // Desuscribir de un vehículo
  private unsubscribeFromVehicle(vehicleId: string): void {
    // Mantener la suscripción si algún mapa lo SIGUE o lo tiene SELECCIONADO.
    // El seleccionado se suscribe por-vehículo para recibir el payload COMPLETO
    // (el card necesita todos los campos en vivo; el all-vehicles es liviano).
    const otherMapNeedsVehicle = this.vehiculoEnUso(vehicleId);

    if (!otherMapNeedsVehicle) {
      // Nadie más necesita este vehículo, desuscribir
      this.wsService.unsubscribeVehicle(vehicleId);
      this.activeSubscriptions.delete(vehicleId);
    }
  }

  //HOOKS
  async ngOnInit() {
    const cantMapasConfig =
      HelperService.getConfigUsuario()?.cantMapasVehiculos || 1;
    this.mapaService.setCantMapas(cantMapasConfig);

    // Cargar seleccionados desde sessionStorage
    const seleccionadosGuardados = this.loadSeleccionadosFromSessionStorage();
    if (seleccionadosGuardados) {
      this.mapaService.setSeleccionadosBarraLateral(seleccionadosGuardados);

      // Suscribirse a vehículos en seguimiento guardados y restaurar vehículo seleccionado
      for (const seleccionado of seleccionadosGuardados) {
        if (seleccionado.followTargetId) {
          this.subscribeToVehicle(seleccionado.followTargetId);
          // Actualizar followTargetId local si es del mapa actual
          if (seleccionado.mapaSeleccionado === this.mapaSeleccionado) {
            this.followTargetId = seleccionado.followTargetId;
          }
        }
        // Restaurar el vehículo seleccionado en reporteSeleccionadoPorMapa para que
        // el marcador HTML se recree al cargar los reportes frescos
        if (seleccionado.reporteSeleccionado && seleccionado.mapaSeleccionado) {
          this.mapaService.setReporteSeleccionadoPorMapa(
            seleccionado.mapaSeleccionado,
            seleccionado.reporteSeleccionado,
          );
          // Suscribir el seleccionado para recibir el payload completo (card en vivo).
          if (seleccionado.reporteSeleccionado.idEntidad) {
            this.idSeleccionadoPorMapa.set(
              seleccionado.mapaSeleccionado,
              seleccionado.reporteSeleccionado.idEntidad,
            );
            this.subscribeToVehicle(seleccionado.reporteSeleccionado.idEntidad);
          }
        }
      }
    }

    // Filtro inicial: el cliente actual del permiso y su jerarquía.
    await this.aplicarFiltroClientePorDefecto();

    await Promise.all([
      this.listarReportes(),
      this.listarUbicaciones(),
      this.listarRecorridos(),
      this.listarRecorridosDinamicos(),
      this.listarConfigMarkers(),
    ]);

    // Configurar suscripción para refrescar reportes
    this.setupRefrescarReportes();

    // Conectar WebSocket y suscribirse a mensajes de reportes en tiempo real
    this.setupWebSocket();

    // Suscripción única a TODOS los vehículos del scope (un mensaje, en vez de
    // suscribir vehículo por vehículo). El backend filtra cada reporte por el
    // permiso activo (nroPermiso = índice x-permiso). El movimiento/recoloreo de
    // cada vehículo llega por WS individual; el polling queda como reconciliación.
    this.wsService.subscribeAllVehicles(LoginService.getIdPermiso());

    // Suscribirse a cambios en seleccionadosBarraLateral para guardar
    // automáticamente en sessionStorage.
    //
    // Con debounce y fuera de la zona: el estado incluye cosas grandes (los
    // reportes de la última búsqueda de recorridos, la traza dibujada), así que
    // el `JSON.stringify` no es gratis. Enganchado directo a cada emisión
    // corría sincrónicamente dentro del click que selecciona un vehículo, antes
    // de dejar pintar. Guardar 500 ms después da exactamente lo mismo.
    const seleccionadosSub = this.mapaService.seleccionadosBarraLateral$
      .pipe(debounceTime(500))
      .subscribe((seleccionados) =>
        this.ngZone.runOutsideAngular(() =>
          this.saveSeleccionadosToSessionStorage(seleccionados),
        ),
      );
    this.subs.add(seleccionadosSub);

    // Suscribirse a cambios en cantMapas para guardar en config de usuario (fire and forget)
    const cantMapasSub = this.mapaService.cantMapas$.subscribe((cant) => {
      // Los mapas nuevos arrancan con el mismo filtro por defecto
      void this.aplicarFiltroClientePorDefecto();

      let configUsuario = HelperService.getConfigUsuario();
      configUsuario
        ? (configUsuario.cantMapasVehiculos = cant)
        : (configUsuario = { cantMapasVehiculos: cant });

      const user: IUpdateUsuario = { config: configUsuario };
      this.usuariosService
        .update(LoginService.getUsuario()._id, user)
        .then((res) => LoginService.setUsuario(res))
        .catch((error) =>
          console.error('Error al guardar cantMapas en usuario:', error),
        );
    });
    this.subs.add(cantMapasSub);

    const sub = this.breakpointObserver
      .observe([Breakpoints.Handset, '(max-width: 800px)'])
      .subscribe((r) => {
        this.isHandset = r.matches;
        this.sidenavMode = this.isHandset ? 'over' : 'side';

        // Mantener la sidebar abierta por defecto:
        // - Si es handset: abrir el overlay (opened = true)
        // - Si es desktop: mostrar la sidebar (hidden = false)
        if (this.isHandset) {
          this.opened = true;
          // hidden no se usa en modo handset pero lo dejamos true para indicar que no es visible por defecto en layout side
          this.hidden = true;
        } else {
          this.opened = false;
          this.hidden = false;
        }

        // informar estado actual
        const isOpen = this.isHandset ? this.opened : !this.hidden;
      });

    this.subs.add(sub);
  }

  ngAfterViewInit() {
    // El tipoMapaBase está disponible a través del getter tipoMapaBaseActual
  }

  ngOnDestroy() {
    // El guardado va con debounce: si se sale del mapa dentro de esa ventana,
    // el último cambio se persiste acá.
    this.saveSeleccionadosToSessionStorage(
      this.mapaService.seleccionadosBarraLateral,
    );

    this.reportes$?.unsubscribe();
    this.ubicaciones$?.unsubscribe();
    this.recorridos$?.unsubscribe();
    this.recorridosDinamicos$?.unsubscribe();
    this.refrescarReportes$?.unsubscribe();
    this.webSocketSub$?.unsubscribe();
    this.detalles$.unsubscribe();

    // Desuscribirse de la suscripción única a todos los vehículos
    this.wsService.unsubscribeAllVehicles();

    // Desuscribirse de TODOS los vehículos trackeados (per-vehículo, compat)
    this.activeSubscriptions.forEach((vehicleId) => {
      this.wsService.unsubscribeVehicle(vehicleId);
    });
    this.activeSubscriptions.clear();

    this.subs.unsubscribe();
  }
}
